import React from 'react';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router';
import { DateAndTimePickerProps } from '../../DateAndTimePicker';
import { DateTimePicker } from '../../DateTimePicker';
interface DateRangeFilterProps {
  t: (key: string) => string;
  filterKey: string;
  beforeValue?: string;
  afterValue?: string;
  onChange?: ({
    value,
    key,
    target,
    index,
  }: {
    value: string | null;
    key: string;
    target: string;
    index?: number;
  }) => void;
  index?: number;
  controls?: DateAndTimePickerProps['controls'];
  isUrlQuerySensitive?: boolean;
  displayFlex?: boolean;
  disabledPx?: boolean;
  hideLabels?: boolean;
}
const DateRangeFilter: React.FC<React.PropsWithChildren<DateRangeFilterProps>> = ({
  t,
  filterKey,
  beforeValue,
  afterValue,
  onChange,
  index,
  isUrlQuerySensitive = false,
  displayFlex = false,
  disabledPx = false,
  hideLabels = false,
}: DateRangeFilterProps) => {
  const afterInputRef = React.useRef<HTMLDivElement>(null);
  const beforeInputRef = React.useRef<HTMLDivElement>(null);
  const query = useLocation().search;
  const urlQueryFilters = JSON.parse(decodeURIComponent(new URLSearchParams(query).get('filter') || '{}'));
  React.useEffect(() => {
    if (isUrlQuerySensitive) {
      if (beforeInputRef?.current && !urlQueryFilters?.created?.before && !beforeValue) {
        const input = beforeInputRef.current.querySelector('input');
        if (input) {
          input.value = '';
        }
      }
      if (afterInputRef?.current && !urlQueryFilters?.created?.after && !afterValue) {
        const input = afterInputRef.current.querySelector('input');
        if (input) {
          input.value = '';
        }
      }
    }
  }, [urlQueryFilters, isUrlQuerySensitive, beforeInputRef, afterInputRef, afterValue, beforeValue]);
  const isValidDate = (value: string) => !isNaN(Date.parse(value));
  const handleDateChange = (target: string) => (val: string | null) => {
    if (!val) {
      onChange?.({ value: null, key: filterKey, target, index });
      return;
    }
    if (isValidDate(val)) {
      onChange?.({ value: new Date(val).toISOString(), key: filterKey, target, index });
    }
  };
  return (
    <Box sx={[{ px: disabledPx ? 0 : 4 }, displayFlex && { display: 'flex', gap: 2 }]}>
      <DateTimePicker
        formLabel={hideLabels ? undefined : t('ns.search_filters:date:after_label')}
        value={afterValue ? afterValue : ''}
        onChange={handleDateChange('after')}
        id={`after-datetime-${filterKey}`}
        slotProps={{
          textField: {
            'aria-label': t('ns.search_filters:date:after_label'),
          },
        }}
        minutesStep={10}
      />
      <DateTimePicker
        formLabel={hideLabels ? undefined : t('ns.search_filters:date:before_label')}
        value={beforeValue ? beforeValue : ''}
        onChange={handleDateChange('before')}
        id={`before-datetime-${filterKey}`}
        slotProps={{
          textField: {
            'aria-label': t('ns.search_filters:date:before_label'),
          },
        }}
        minutesStep={10}
      />
    </Box>
  );
};
export { DateRangeFilter };
